/**
 * Custom hook for managing custom icon uploads, listing, and deletion
 */

import { useState, useCallback } from 'react';
import { 
  uploadIcon, 
  listIcons, 
  deleteIcon, 
  validateImageFile 
} from '../../../../../services/iconService';

export const useCustomIcons = (onIconDeleted) => {
  const [customIcons, setCustomIcons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingIconId, setDeletingIconId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Load custom icons from API
  const loadIcons = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listIcons();
      setCustomIcons(result.icons || []);
    } catch (err) {
      console.error('Failed to load custom icons:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle file upload
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    
    // Validate file
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    
    try {
      const result = await uploadIcon(file, setUploadProgress);
      setCustomIcons(prev => [{
        iconId: result.iconId,
        publicUrl: result.publicUrl,
        fileName: result.fileName,
        mimeType: result.mimeType,
      }, ...prev]);
      setUploadProgress(100);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, []);

  // Show delete confirmation
  const requestDelete = useCallback((iconId, e) => {
    e?.stopPropagation();
    if (deletingIconId) return;
    setConfirmDeleteId(iconId);
  }, [deletingIconId]);

  // Confirm and execute delete
  const confirmDelete = useCallback(async (iconId) => {
    setConfirmDeleteId(null);
    setDeletingIconId(iconId);
    try {
      await deleteIcon(iconId);
      setCustomIcons(prev => prev.filter(i => i.iconId !== iconId));
      onIconDeleted?.(iconId);
    } catch (err) {
      console.error('Delete failed:', err);
      setUploadError(err.message);
    } finally {
      setDeletingIconId(null);
    }
  }, [onIconDeleted]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setConfirmDeleteId(null);
  }, []);

  // Clear upload error
  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    // State
    customIcons,
    isLoading,
    error,
    uploadProgress,
    uploadError,
    isUploading,
    deletingIconId,
    confirmDeleteId,
    // Actions
    loadIcons,
    handleUpload,
    requestDelete,
    confirmDelete,
    cancelDelete,
    clearUploadError,
  };
};

export default useCustomIcons;

